import { type ReactNode, useEffect, useRef } from 'react';

type DrawerPosition = 'right' | 'bottom';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  position?: DrawerPosition;
  children: ReactNode;
  className?: string;
}

const positionStyles: Record<DrawerPosition, string> = {
  right: 'ml-auto h-full w-full max-w-[400px] rounded-l-modal',
  bottom: 'mt-auto w-full max-h-[80vh] rounded-t-modal',
};

const slideIn: Record<DrawerPosition, string> = {
  right: 'translate-x-0',
  bottom: 'translate-y-0',
};

const slideOut: Record<DrawerPosition, string> = {
  right: 'translate-x-full',
  bottom: 'translate-y-full',
};

export function Drawer({ open, onClose, position = 'right', children, className = '' }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" aria-modal="true" role="dialog">
      <div
        className="absolute inset-0 bg-foreground/40 transition-opacity duration-200"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        className={[
          'relative border border-border bg-background shadow-lg overflow-y-auto',
          'transition-transform duration-350 ease-out',
          positionStyles[position],
          open ? slideIn[position] : slideOut[position],
          className,
        ].join(' ')}
      >
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
